import React, { useState, useEffect, useRef } from 'react';
import Iframe from 'react-iframe';
import { connect } from 'react-redux';
import { Form, Input, Checkbox, Message } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { apiService } from '../../services';
import { fixID } from '../../helpers';
import { UpdateError, CollectPaymentError, ExelotError } from '../../helpers/fetchErrors';
import { PaymentPageOpts } from '../../helpers/payments';
import { getSmsFeePaymentConfigByPublicPath, resolveSmsFeeTrackingRef, formatHoursLeftForDisplay, formatPriceShekels } from '../../config/smsFeePaymentPages';

function unwrapApiData(res) {
  if (res && typeof res.data === 'object' && res.data !== null && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
}

const SmsFeePaymentPage = (props) => {
  const {
    history,
    getSmsFeePaymentParcelAPI,
    updateSmsFeePaymentParcelAPI,
    collectSmsFeePaymentParcelAPI,
    location: { pathname },
    match: { params }
  } = props;

  const [email, setEmail] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [parcelId, setParcelId] = useState(null);
  const [parcelData, setParcelData] = useState(null);
  const [error, setError] = useState();
  const [creditGuardUrl, setCreditGuardUrl] = useState();
  const pageConfigRef = useRef(null);

  const publicPath = pathname.split('/').filter(Boolean)[0];
  const pageConfig = getSmsFeePaymentConfigByPublicPath(publicPath);

  useEffect(() => {
    pageConfigRef.current = pageConfig;
  }, [pageConfig]);

  function handleCGPaymentCompletion(evt) {
    const payload = evt && evt.data;
    if (!payload) {
      console.error('No postMessage data');
      return;
    }
    const { status, page, res } = payload;
    const cfg = pageConfigRef.current;
    if (!cfg || page !== cfg.apiPageKey) {
      console.error(`Unexpected page ${page} for SMS fee flow`);
      return;
    }
    if (PaymentPageOpts[page] !== PaymentPageOpts[cfg.paymentPageOptsKey]) {
      console.error(`PaymentPageOpts mismatch for ${page}`);
      return;
    }
    history.push(`/${cfg.publicPath}/callback/${status}`, { ...res });
  }

  useEffect(() => {
    window.addEventListener('message', handleCGPaymentCompletion, false);
    return () => window.removeEventListener('message', handleCGPaymentCompletion, false);
  }, [history]);

  useEffect(() => {
    if (!pageConfig) {
      setError('סוג תשלום לא מוכר. אנא בדקו את כתובת הקישור.');
      return;
    }

    const id = fixID(params.parcelID);
    if (!id) {
      setError('תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.');
      return;
    }

    setParcelId(id);

    apiService
      .callServer(
        getSmsFeePaymentParcelAPI,
        { ':page:': pageConfig.apiPageKey, ':id:': id },
        null,
        null,
        null,
        true
      )
      .then((res) => {
        setParcelData(unwrapApiData(res));
      })
      .catch(() => {
        setError('תקלה בקבלת פרטי החבילה. אנא ודאו שכתובת הדף הוכנסה במלואה.');
      });
  }, [pageConfig, params.parcelID, getSmsFeePaymentParcelAPI]);

  const handleEmail = (e, { value }) => {
    setEmail(value);
  };

  const handleConfirm = () => {
    setConfirmed(!confirmed);
  };

  function updateEmailIfProvided() {
    const trimmed = (email || '').trim();
    if (!trimmed) {
      return Promise.resolve();
    }
    return apiService
      .callServer(
        updateSmsFeePaymentParcelAPI,
        { ':page:': pageConfig.apiPageKey, ':id:': parcelId },
        null,
        JSON.stringify({ email: trimmed }),
        null,
        true
      )
      .catch(() => {
        throw new UpdateError();
      });
  }

  function collectPayment() {
    const page = pageConfig.apiPageKey;
    const baseCreditGuardCallbackUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/parcels/creditguard/process_payment?page=${page}&status=:status:`;
    const creditGuardCallbackUrls = {
      success_url: baseCreditGuardCallbackUrl.replace(':status:', 'success'),
      error_url: baseCreditGuardCallbackUrl.replace(':status:', 'error'),
      cancel_url: baseCreditGuardCallbackUrl.replace(':status:', 'cancelled')
    };

    return apiService
      .callServer(
        collectSmsFeePaymentParcelAPI,
        { ':page:': page, ':id:': parcelId },
        null,
        JSON.stringify({ ...creditGuardCallbackUrls }),
        null,
        true
      )
      .then((res) => {
        const out = unwrapApiData(res);
        const url = out && out.url;
        if (!url) {
          throw new CollectPaymentError();
        }
        setCreditGuardUrl(url);
      })
      .catch(() => {
        throw new CollectPaymentError();
      });
  }

  const handleSubmit = () => {
    setSubmitted(true);

    updateEmailIfProvided()
      .then(collectPayment)
      .catch((err) => {
        if (err instanceof ExelotError) {
          setError(err.message);
          return;
        }
        console.error(err);
        setError(new UpdateError().message);
      });
  };

  function renderError() {
    if (!error) {
      return null;
    }
    return <Message className='error-message' error content={error} />;
  }

  if (!pageConfig) {
    return (
      <div>
        {renderError()}
      </div>
    );
  }

  if (!parcelData) {
    return renderError() || null;
  }

  if (creditGuardUrl) {
    return (
      <Iframe
        url={creditGuardUrl}
        className='cg-iframe'
      />
    );
  }

  const { copy } = pageConfig;
  const hoursLeft = parcelData.hoursLeft;
  const hasTime = hoursLeft !== null && hoursLeft !== undefined && hoursLeft > 0;
  const trackingRef = resolveSmsFeeTrackingRef(parcelData);
  const hoursDisplay = formatHoursLeftForDisplay(hoursLeft);
  const priceDisplay = formatPriceShekels(parcelData.price);

  return (
    <div>
      {renderError()}
      {hasTime
        ? (
          <div className='sms-content-wrap'>
            <p className='sms-message'>
              {copy.formIntro({
                trackingRef,
                priceDisplay,
                hoursDisplay
              })}
            </p>

            <Form className='sms-form rtl' onSubmit={handleSubmit}>
              <Form.Field>
                <label htmlFor='email'>אימייל:</label>
                <Input type='email' name='email' placeholder='אימייל' id='email' value={email} onChange={handleEmail} style={{ direction: 'ltr' }} />
              </Form.Field>
              <Form.Field inline>
                <Checkbox name='confirm' className='rtl' required checked={confirmed} onChange={handleConfirm} />
                <label className='confirm-label' htmlFor='confirm'>אני מאשר כי פרטי המשלוח הם:</label>
                <div className='address-wrap'>
                  {parcelData.name}
                  <br />
                  {parcelData.description}
                </div>
              </Form.Field>
              <Form.Field inline>
                <label>מס&apos; למעקב:</label>
                <span>{trackingRef}</span>
              </Form.Field>
              <div className='centered'>
                <button className='btn medium orange' type='submit' disabled={!confirmed || submitted}>המשך לתשלום</button>
              </div>
            </Form>
          </div>
        ) : (
          <div className='sms-content-wrap'>
            <h2 className='sms-message'>
              {copy.expiredTitle}
            </h2>
          </div>
        )}
    </div>
  );
};

function mapStateToProps(state) {
  const {
    getSmsFeePaymentParcel,
    updateSmsFeePaymentParcel,
    collectSmsFeePaymentParcel
  } = state.api;

  return {
    getSmsFeePaymentParcelAPI: getSmsFeePaymentParcel,
    updateSmsFeePaymentParcelAPI: updateSmsFeePaymentParcel,
    collectSmsFeePaymentParcelAPI: collectSmsFeePaymentParcel
  };
}

const SmsFeePaymentPageRouter = withRouter(connect(mapStateToProps)(SmsFeePaymentPage));
export { SmsFeePaymentPageRouter as SmsFeePaymentPage };
